import moment from "./moment.js";

/**
 * Format a date as a numeral date (e.g., 5/3/2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsNumeralDate(date) {
	return _format(date, "L");
}

/**
 * Format a date as an ISO date and time (e.g., 2020-05-03T17:58:00).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsIsoDateAndTime(date) {
	return _format(date, "YYYY-MM-DDTHH:mm:ss");
}

/**
 * Format a date as a short date (e.g., Sep 4, 1986).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsShortDate(date) {
	return _format(date, "ll");
}

/**
 * Format a date as a long date (e.g., September 4, 1986).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsLongDate(date) {
	return _format(date, "LL");
}

/**
 * Format a date as a date and time (e.g., Sep 4, 1986 8:30 PM).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsDateAndTime(date) {
	return _format(date, "LLL");
}

/**
 * Format a date as a long date and time (e.g., Thursday, September 4, 1986 8:30 PM +01:00).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsLongDateAndTime(date) {
	return _format(date, "LLLL Z");
}

/**
 * Format as day and month (e.g., 4 Sep).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsDayAndMonth(date) {
	return _format(date, "D MMM");
}

/**
 * Format as week number (e.g., 23).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsWeekNumber(date) {
	return _format(date, "w");
}

/**
 * Format as month (e.g., Sep).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsMonth(date) {
	return _format(date, "MMM");
}

/**
 * Format as relative date (e.g., 3 days ago).
 *
 * @param {Date} date - The date to format
 * @param {Date} referenceDate - The date from which to calculate the relative
 *   date. If not provided, the relative date will be calculated from the now
 * @return {string}
 */
export function formatAsRelativeDate(date, referenceDate) {
	if (referenceDate) {
		return moment(date).from(referenceDate);
	}

	return moment(date).fromNow();
}

/**
 * Format as ISO 8601 date (e.g., 2020-05-03).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsDashedYearMonthDay(date) {
	return _format(date, "YYYY-MM-DD");
}

/**
 * Format as year (e.g., 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsYear(date) {
	return _format(date, "YYYY");
}

/**
 * Format as half year (e.g., H1). For the last half of the year, the year is
 * also included (e.g., H2 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsHalfYearWithOptionalYear(date) {
	return moment(date).quarter() <= 2 ? "H1" : `H2 ${formatAsYear(date)}`;
}

/**
 * Format as quarter and year (e.g., Q1 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsQuarterAndYear(date) {
	return _format(date, "[Q]Q YYYY");
}

/**
 * Format as bimester and year (e.g., Jan - Feb 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsBimesterAndYear(date) {
	const endDate = moment(date).add(1, "Month").toDate();

	return `${_format(date, "MMM")} - ${_format(endDate, "MMM YYYY")}`;
}

/**
 * Format as month and year (e.g., Sep 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsMonthAndYear(date) {
	return _format(date, "MMM YYYY");
}

/**
 * Format as long month and year (e.g., September 2020).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsLongMonthAndYear(date) {
	return _format(date, "MMMM YYYY");
}

/**
 * Format as month and short year (e.g., Sep 20).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsMonthAndShortYear(date) {
	return _format(date, "MMM YY");
}

/**
 * Format as year, month and day un-dashed (e.g., 20200904).
 *
 * @param {Date} date - The date to format
 * @return {string}
 */
export function formatAsFullYearMonthAndDay(date) {
	return _format(date, "YYYYMMDD");
}

function _format(date, format) {
	return moment(date).format(format);
}
